import React from 'react';
import type { TableComponent } from '../../schemas/table';
import { cn } from '../../utils/cn';

interface TableRendererProps {
    component: TableComponent;
    className?: string;
}

/**
 * Render a table component using ShadCN-style table
 */
export function TableRenderer({ component, className }: TableRendererProps) {
    const { title, columns, rows, sortable, paginated } = component;

    return (
        <div className={cn('w-full overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700', className)}>
            {title && (
                <div className="border-b border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-800">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                        {title}
                    </h3>
                </div>
            )}
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                        <tr>
                            {columns.map((column) => (
                                <th
                                    key={column.key}
                                    scope="col"
                                    className={cn(
                                        'px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400',
                                        sortable && 'cursor-pointer hover:text-gray-700 dark:hover:text-gray-200'
                                    )}
                                >
                                    {column.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-900">
                        {rows.map((row, rowIndex) => (
                            <tr
                                key={rowIndex}
                                className="hover:bg-gray-50 dark:hover:bg-gray-800"
                            >
                                {columns.map((column) => (
                                    <td
                                        key={column.key}
                                        className="whitespace-nowrap px-4 py-3 text-sm text-gray-900 dark:text-gray-100"
                                    >
                                        {formatCellValue(row[column.key], column.type)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {paginated && rows.length > 10 && (
                <div className="border-t border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-800">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        Showing {Math.min(10, rows.length)} of {rows.length} rows
                    </p>
                </div>
            )}
        </div>
    );
}

/**
 * Format cell value based on column type
 */
function formatCellValue(value: unknown, type?: string): React.ReactNode {
    if (value === null || value === undefined) return '—';

    switch (type) {
        case 'number':
            return typeof value === 'number' ? value.toLocaleString() : String(value);
        case 'date':
            return typeof value === 'string' ? new Date(value).toLocaleDateString() : String(value);
        default:
            return String(value);
    }
}
